import React from 'react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@codes/ui-kit/components/ui/card';

interface PerformanceMetricsTableProps {
    data: any[];
    colors: string[];
}

export const PerformanceMetricsTable: React.FC<PerformanceMetricsTableProps> = ({ data, colors }) => {
    const { t } = useTranslation();

    if (data.length === 0) {
        return null;
    }

    const getPerformanceStatus = (ms: number) => {
        if (ms <= 150) {
            return {
                status: t('logs.table.excellent'),
                color: 'text-green-600 bg-green-50',
                icon: '🟢',
            };
        }
        if (ms <= 300) {
            return {
                status: t('logs.table.good'),
                color: 'text-blue-600 bg-blue-50',
                icon: '🔵',
            };
        }
        if (ms <= 500) {
            return {
                status: t('logs.table.average'),
                color: 'text-yellow-600 bg-yellow-50',
                icon: '🟡',
            };
        }
        return {
            status: t('logs.table.needsImprovement'),
            color: 'text-red-600 bg-red-50',
            icon: '🔴',
        };
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-xl font-bold">{t('logs.charts.performanceMetrics')}</CardTitle>
                <CardDescription className="text-sm text-sub">
                    {t('logs.charts.performanceMetricsDesc')}
                </CardDescription>
            </CardHeader>
            <CardContent>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-border">
                                <th className="text-left py-3 px-4 font-semibold text-sm">{t('logs.table.service')}</th>
                                <th className="text-right py-3 px-4 font-semibold text-sm">
                                    {t('logs.table.apiCalls')}
                                </th>
                                <th className="text-right py-3 px-4 font-semibold text-sm">
                                    {t('logs.table.avgResponseTime')}
                                </th>
                                <th className="text-center py-3 px-4 font-semibold text-sm">
                                    {t('logs.table.performanceStatus')}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {[...data]
                                .sort((a, b) => b[t('logs.charts.apiCallLabel')] - a[t('logs.charts.apiCallLabel')])
                                .map((item, index) => {
                                    const responseTime = item[t('logs.charts.avgResponseTimeLabel')];
                                    const perfStatus = getPerformanceStatus(responseTime);

                                    return (
                                        <tr key={item.name} className="border-b border-border hover:bg-box">
                                            <td className="py-3 px-4">
                                                <div className="flex items-center gap-2">
                                                    <div
                                                        className="w-3 h-3 rounded-full"
                                                        style={{
                                                            backgroundColor: colors[index % colors.length],
                                                        }}
                                                    ></div>
                                                    <span className="font-medium">{item.fullName}</span>
                                                </div>
                                            </td>
                                            <td className="py-3 px-4 text-right font-mono">
                                                <span className="font-semibold text-blue-600">
                                                    {item[t('logs.charts.apiCallLabel')].toLocaleString()}
                                                </span>
                                                <div className="text-xs text-text-500">
                                                    {t('logs.statistics.calls')}
                                                </div>
                                            </td>
                                            <td className="py-3 px-4 text-right font-mono">
                                                <span className="font-semibold">{responseTime}</span>
                                                <span className="text-xs text-text-500 ml-1">
                                                    {t('logs.table.timeUnit.milliseconds')}
                                                </span>
                                                <div className="w-full bg-default rounded-full h-1.5 mt-1">
                                                    <div
                                                        className="bg-gradient-to-r from-green-400 to-blue-500 h-1.5 rounded-full transition-all duration-300"
                                                        style={{
                                                            width: `${Math.min((responseTime / 500) * 100, 100)}%`,
                                                        }}
                                                    ></div>
                                                </div>
                                            </td>
                                            <td className="py-3 px-4 text-center">
                                                <div
                                                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${perfStatus.color}`}
                                                >
                                                    <span>{perfStatus.icon}</span>
                                                    <span>{perfStatus.status}</span>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                        </tbody>
                    </table>
                </div>
                <div className="mt-4 p-3 bg-box rounded-lg">
                    <div className="text-sm text-sub mb-2">{t('logs.table.performanceStandard')}</div>
                    <div className="flex flex-wrap gap-4 text-xs">
                        <span className="flex items-center gap-1">
                            <span>🟢</span> {t('logs.table.excellent')}: ≤150ms
                        </span>
                        <span className="flex items-center gap-1">
                            <span>🔵</span> {t('logs.table.good')}: ≤300ms
                        </span>
                        <span className="flex items-center gap-1">
                            <span>🟡</span> {t('logs.table.average')}: ≤500ms
                        </span>
                        <span className="flex items-center gap-1">
                            <span>🔴</span> {t('logs.table.needsImprovement')}: {t('logs.table.over500ms')}
                        </span>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};
